'use client';

import { useState, useEffect, useMemo } from 'react';
import { Trophy, Medal, Globe, MapPin, ArrowRight, Crown } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { View } from '../../types';
import { fetchViews } from '../../lib/api';
import ViewCard from '../../components/ViewCard';
import { ViewSkeleton } from '../../components/ViewSkeleton';
import { HoverScrollText } from '../../components/HoverScrollText';
import { RecordsPageSEO } from './SEO';

// Countries for the country records section
const PROMINENT_COUNTRIES = [
  'United States',
  'England',
  'Scotland',
  'France',
  'New Zealand',
  'China',
  'Japan',
  'India',
  'Australia',
  'Germany',
  'Spain',
  'Italy',
  'Switzerland',
  'Canada'
];

// Continents (using Australasia which is the normalized form of Oceania)
const CONTINENTS = ['Europe', 'Asia', 'North America', 'South America', 'Australasia', 'Africa'];

// Parse distance string "XXX km / YYY mi" to numeric km value
const parseDistanceKm = (distance: string): number => {
  const distanceStr = distance.split(' ')[0];
  return Number.parseFloat(distanceStr) || 0;
};

// Sort views by distance (descending - farthest first)
const sortByDistanceDesc = (views: View[]): View[] => {
  return [...views].sort((a, b) => parseDistanceKm(b.distance) - parseDistanceKm(a.distance));
};

// Normalize continent for matching (handle Oceania -> Australasia)
const normalizeContinent = (continent: string | undefined): string => {
  if (!continent) return '';
  if (continent === 'Oceania') return 'Australasia';
  return continent;
};

// Check if a view matches a country (either source or destination)
const viewMatchesCountry = (view: View, country: string): boolean => {
  // Handle various country name formats
  const normalizedCountry = country.toLowerCase();
  const sourceCountry = (view.country || '').toLowerCase();
  const destCountry = (view.destinationCountry || '').toLowerCase();

  // Direct match
  if (sourceCountry === normalizedCountry || destCountry === normalizedCountry) {
    return true;
  }

  // Handle common aliases
  if (normalizedCountry === 'united states') {
    return sourceCountry === 'usa' || sourceCountry === 'us' || sourceCountry === 'united states' ||
           destCountry === 'usa' || destCountry === 'us' || destCountry === 'united states';
  }
  if (normalizedCountry === 'england') {
    return sourceCountry === 'england' || destCountry === 'england';
  }

  return false;
};

// Medal styling for podium positions
const getMedalStyle = (rank: 1 | 2 | 3) => {
  switch (rank) {
    case 1:
      return {
        bg: 'bg-gradient-to-br from-yellow-400/20 to-amber-600/20',
        border: 'border-yellow-500/50',
        medal: 'Gold',
        icon: 'text-yellow-400',
        ring: 'ring-yellow-500/30',
        shadow: 'shadow-yellow-500/20'
      };
    case 2:
      return {
        bg: 'bg-gradient-to-br from-gray-300/20 to-gray-500/20',
        border: 'border-gray-400/50',
        medal: 'Silver',
        icon: 'text-gray-300',
        ring: 'ring-gray-400/30',
        shadow: 'shadow-gray-400/20'
      };
    case 3:
      return {
        bg: 'bg-gradient-to-br from-orange-400/20 to-amber-700/20',
        border: 'border-orange-500/50',
        medal: 'Bronze',
        icon: 'text-orange-400',
        ring: 'ring-orange-500/30',
        shadow: 'shadow-orange-500/20'
      };
  }
};

interface RecordsPageProps {
  onViewClick?: (view: View) => void;
  onShare?: (view: View) => void;
}

export default function RecordsPage({ onViewClick, onShare }: RecordsPageProps) {
  const [views, setViews] = useState<View[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch views data
  useEffect(() => {
    const loadViews = async () => {
      try {
        setIsLoading(true);
        const data = await fetchViews();
        setViews(data);
      } catch (err) {
        setError('Failed to load views data');
        console.error('Error fetching views:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadViews();
  }, []);

  // Filter to only confirmed views for records
  const confirmedViews = useMemo(() => {
    return views.filter(view => view.status?.toLowerCase() === 'confirmed');
  }, [views]);

  // Computed records (using confirmed views only)
  const overallTop3 = useMemo(() => {
    return sortByDistanceDesc(confirmedViews).slice(0, 3);
  }, [confirmedViews]);

  const skylineTop3 = useMemo(() => {
    const skylines = confirmedViews.filter(view => {
      const viewType = view.type;
      if (Array.isArray(viewType)) {
        return viewType.some(t => t.toLowerCase().includes('skyline'));
      }
      return (viewType || '').toLowerCase().includes('skyline');
    });
    return sortByDistanceDesc(skylines).slice(0, 3);
  }, [confirmedViews]);

  const continentRecords = useMemo(() => {
    const result: Record<string, View | null> = {};

    for (const continent of CONTINENTS) {
      const continentViews = confirmedViews.filter(view => {
        const sourceContinent = normalizeContinent(view.continent);
        const destContinent = normalizeContinent(view.destinationContinent);
        return sourceContinent === continent || destContinent === continent;
      });
      const sorted = sortByDistanceDesc(continentViews);
      result[continent] = sorted[0] || null;
    }

    return result;
  }, [confirmedViews]);

  const countryRecords = useMemo(() => {
    const result: Record<string, View | null> = {};

    for (const country of PROMINENT_COUNTRIES) {
      const countryViews = confirmedViews.filter(view => viewMatchesCountry(view, country));
      const sorted = sortByDistanceDesc(countryViews);
      result[country] = sorted[0] || null;
    }

    return result;
  }, [confirmedViews]);

  const handleViewClick = (view: View) => {
    if (onViewClick) {
      onViewClick(view);
    }
  };

  const handleShare = (e: React.MouseEvent, view: View) => {
    e.stopPropagation();
    if (onShare) {
      onShare(view);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="text-center py-12">
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <RecordsPageSEO />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Page Header - responsive */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400" />
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text">World Records</h1>
            <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400" />
          </div>
          <p className="text-muted-foreground text-base sm:text-lg px-4">
            The farthest distance line-of-sight views on Earth
          </p>
        </div>

        {/* Section 1: Overall Champions */}
        <section className="mb-12 sm:mb-16">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Crown className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-400" />
            <h2 className="text-xl sm:text-2xl font-semibold">Champions</h2>
            <span className="text-muted-foreground text-xs sm:text-sm">Top 3 Farthest Views</span>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {[1, 2, 3].map((i) => (
                <ViewSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {/* Sequential order: #1, #2, #3 */}
              {[0, 1, 2].map((index) => {
                const view = overallTop3[index];
                if (!view) return null;
                const rank = (index + 1) as 1 | 2 | 3;
                const style = getMedalStyle(rank);

                return (
                  <div
                    key={view.id}
                    className={cn(
                      'relative rounded-lg p-1 ring-2 mt-2 sm:mt-0',
                      style.bg,
                      style.ring
                    )}
                  >
                    {/* Medal badge - responsive */}
                    <div className={cn(
                      'absolute -top-2.5 sm:-top-3 left-1/2 -translate-x-1/2 z-10',
                      'flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full',
                      'bg-background border',
                      style.border
                    )}>
                      <Medal className={cn('h-3 w-3 sm:h-4 sm:w-4', style.icon)} />
                      <span className={cn('text-xs sm:text-sm font-semibold', style.icon)}>
                        #{rank}
                      </span>
                    </div>

                    <ViewCard
                      view={view}
                      onClick={() => handleViewClick(view)}
                      onShare={(e) => handleShare(e, view)}
                      isCompact={false}
                    />
                    {/* Credit */}
                    {view.resource_4 && (
                      <div className="text-center text-xs text-muted-foreground mt-2 pb-1">
                        Credit: {view.resource_4}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Section 2: Skyline / Man-made Structure Records */}
        <section className="mb-12 sm:mb-16">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Trophy className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-400" />
            <h2 className="text-xl sm:text-2xl font-semibold">Skyline / Man-made Structure Records</h2>
            <span className="text-muted-foreground text-xs sm:text-sm">Top 3 Farthest Skyline Views</span>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {[1, 2, 3].map((i) => (
                <ViewSkeleton key={i} />
              ))}
            </div>
          ) : skylineTop3.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No skyline views found</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {skylineTop3.map((view, index) => {
                const rank = (index + 1) as 1 | 2 | 3;
                const style = getMedalStyle(rank);

                return (
                  <div
                    key={view.id}
                    className={cn(
                      'relative rounded-lg p-1 ring-2 mt-2 sm:mt-0',
                      style.bg,
                      style.ring
                    )}
                  >
                    {/* Medal badge - responsive */}
                    <div className={cn(
                      'absolute -top-2.5 sm:-top-3 left-1/2 -translate-x-1/2 z-10',
                      'flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full',
                      'bg-background border',
                      style.border
                    )}>
                      <Medal className={cn('h-3 w-3 sm:h-4 sm:w-4', style.icon)} />
                      <span className={cn('text-xs sm:text-sm font-semibold', style.icon)}>
                        #{rank}
                      </span>
                    </div>

                    <ViewCard
                      view={view}
                      onClick={() => handleViewClick(view)}
                      onShare={(e) => handleShare(e, view)}
                      isCompact={false}
                    />
                    {/* Credit */}
                    {view.resource_4 && (
                      <div className="text-center text-xs text-muted-foreground mt-2 pb-1">
                        Credit: {view.resource_4}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Section 3: Continental Records */}
        <section className="mb-12 sm:mb-16">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Globe className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            <h2 className="text-xl sm:text-2xl font-semibold">Continental Records</h2>
            <span className="text-muted-foreground text-xs sm:text-sm">Farthest View by Continent</span>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-28 sm:h-32 bg-muted/30 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {CONTINENTS.map((continent) => {
                const view = continentRecords[continent];

                return (
                  <div
                    key={continent}
                    className={cn(
                      'bg-card border border-border rounded-lg p-3 sm:p-4 min-h-[44px]',
                      'hover:border-primary/50 transition-colors',
                      view && 'cursor-pointer active:scale-[0.98]'
                    )}
                    onClick={() => view && handleViewClick(view)}
                    onKeyDown={(e) => e.key === 'Enter' && view && handleViewClick(view)}
                    role={view ? 'button' : undefined}
                    tabIndex={view ? 0 : undefined}
                  >
                    <div className="flex items-start justify-between mb-1.5 sm:mb-2">
                      <h3 className="font-semibold text-base sm:text-lg">{continent}</h3>
                      <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                    </div>

                    {view ? (
                      <div className="space-y-1.5 sm:space-y-2">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <span className="text-lg sm:text-xl font-bold text-primary">
                            {view.distanceKm.toLocaleString()} km
                          </span>
                          <span className="text-xs sm:text-sm text-muted-foreground">
                            / {view.distanceMi.toLocaleString()} mi
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate max-w-[45%]">{view.source}</span>
                          <ArrowRight className="h-3 w-3 mx-0.5 sm:mx-1 flex-shrink-0" />
                          <span className="truncate max-w-[45%]">{view.destination}</span>
                        </div>
                        {view.resource_4 && (
                          <div className="text-[10px] sm:text-xs text-muted-foreground pt-0.5 sm:pt-1">
                            Credit: {view.resource_4}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs sm:text-sm text-muted-foreground">No records found</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Section 4: Country Records */}
        <section className="mb-8">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            <h2 className="text-xl sm:text-2xl font-semibold">Country Records</h2>
            <span className="text-muted-foreground text-xs sm:text-sm">Farthest View by Country</span>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-12 bg-muted/30 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {PROMINENT_COUNTRIES.map((country) => {
                const view = countryRecords[country];

                return (
                  <div
                    key={country}
                    className={cn(
                      'flex items-center justify-between',
                      'bg-card/50 border border-border rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 min-h-[44px]',
                      'hover:border-primary/50 hover:bg-card transition-colors',
                      view && 'cursor-pointer active:scale-[0.99]'
                    )}
                    onClick={() => view && handleViewClick(view)}
                    onKeyDown={(e) => e.key === 'Enter' && view && handleViewClick(view)}
                    role={view ? 'button' : undefined}
                    tabIndex={view ? 0 : undefined}
                  >
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className="font-medium text-sm sm:text-base truncate">{country}</span>
                    </div>

                    {view ? (
                      <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm ml-2">
                        <span className="font-semibold text-primary whitespace-nowrap min-w-[80px] text-right">
                          {view.distanceKm.toLocaleString()} km
                        </span>
                        {/* Clipped at 200px - hovering slides it sideways so the
                            full route can be read without opening the record */}
                        <HoverScrollText
                          className="text-muted-foreground hidden sm:inline-block max-w-[200px]"
                          title={`${view.source} → ${view.destination}`}
                        >
                          {view.source} <ArrowRight className="h-3 w-3 inline mx-1" /> {view.destination}
                        </HoverScrollText>
                        <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                      </div>
                    ) : (
                      <span className="text-xs sm:text-sm text-muted-foreground">No record</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
